/* eslint-disable react/display-name */

/* eslint-disable promise/always-return */
import React, { useCallback, useEffect, useState } from 'react';

import startCase from 'lodash/startCase';

import { Flex } from '@qality/common-components/src/atoms/Flex';
import { Text } from '@qality/common-components/src/atoms/Text';
import { SpinnerLoader } from '@qality/common-components/src/molecules/SpinnerLoader';
import { BarChart } from '@qality/common-components/src/molecules/charts/BarChart';
import { PieChart } from '@qality/common-components/src/molecules/charts/PieChart';
import { messages } from '@qality/common-messages';
import {
  StorageOfUniqueModels,
  StorageOfUniqueServices,
  UserService,
} from '@qality/common-service';
import TestExecutionReportService from '@qality/common-service/src/TestExecutionReportService';
import { CssUtils } from '@qality/common-utils';

import { convertToBarChart, convertToPieChart } from './ReportChart.util';

const options = [
  {
    value: 'statusChart',
    apiFields: ['status'],
    chartType: 'pie',
  },
  {
    value: 'assigneeChart',
    apiFields: ['executionAssignee', 'status'],
    chartType: 'bar',
  },
];

export type Props = {
  chartName: 'statusChart' | 'assigneeChart';
  filterOptions?: {
    name: string;
    value: string | number;
  }[];
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
};

export function ReportChart({
  chartName,
  filterOptions,
  isLoading,
  setIsLoading,
}: Props): JSX.Element {
  const selectedChartType = options.find((o) => o.value === chartName);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState<{ message: string }>(null);
  const User = StorageOfUniqueModels.get('User');

  const mapDataSeriesValues = ({ axis, dataSeries }, axisType, mapping) => {
    const updatedAxis = axis
      ? [...axis].map((axisValue) => {
          switch (axisType) {
            case 'executionAssignee':
              if (!axisValue) {
                return 'Unassigned';
              }
              return (
                mapping.exectuionAssignee
                  .find((assignee) => assignee?.getIdentifier() === axisValue)
                  ?.getDisplayName() || axisValue
              );
            default:
              return axisValue;
          }
        })
      : axis;

    const sum = dataSeries[0]?.data
      .filter((currentObject) => currentObject?.value !== undefined)
      .reduce(
        (accumulator, currentObject) => accumulator + currentObject.value,
        0
      );

    const updatedDataSeries = [...dataSeries]
      .sort((a, b) => {
        if (a.stack === 'status' && b.stack === 'status') {
          return a.name.position - b.name.position;
        }
      })
      .map((data) => {
        if (data.stack) {
          switch (data.stack) {
            case 'status':
              return {
                ...data,
                name: startCase(data.name.name),
                itemStyle: {
                  color: CssUtils.getTokenHexValue(
                    data.name.color.backgroundToken
                  ),
                },
              };
          }
        } else {
          switch (data.name) {
            case 'status':
              return {
                ...data,
                data: data.data
                  .sort((a, b) => a.name.position - b.name.position)
                  .map((status) => ({
                    ...status,
                    name: startCase(status.name.name),
                    itemStyle: {
                      color: CssUtils.getTokenHexValue(
                        status.name.color.backgroundToken
                      ),
                    },
                    percentage:
                      sum !== 0
                        ? ((status.value / sum) * 100).toFixed(2)
                        : undefined,
                  })),
              };
          }
        }
        return data;
      });

    return {
      axis: updatedAxis,
      dataSeries: updatedDataSeries,
    };
  };

  const fetchReportData = useCallback(async (chartType, filters) => {
    setError(null);
    setIsLoading(true);
    try {
      await TestExecutionReportService.getReportCharts([
        ...filters,
        { name: 'fields', value: chartType.apiFields.join(',') },
      ]).then(async (data) => {
        let convertedReportData;
        if (chartType.chartType === 'pie') {
          convertedReportData = convertToPieChart(data, chartType.apiFields);
        } else {
          convertedReportData = convertToBarChart(data, chartType.apiFields);
        }

        const mapping: {
          exectuionAssignee?: typeof User[];
        } = {};

        if (chartType.apiFields.includes('executionAssignee')) {
          mapping.exectuionAssignee = await Promise.all(
            convertedReportData.axis.filter(Boolean).map((userId) => {
              return UserService.getUserById(userId);
            })
          );
        }

        setReportData(
          mapDataSeriesValues(
            convertedReportData,
            // apiField[0] is an X axis key
            chartType.apiFields[0],
            mapping
          )
        );
      });
    } catch (e) {
      setError({
        message: 'Failed to fetch Chart data',
      });
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['error.report.loading.message']
      );
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchReportData(selectedChartType, filterOptions);
  }, [selectedChartType, filterOptions]);

  let ChartComponent;

  switch (selectedChartType.chartType) {
    case 'pie':
      ChartComponent = PieChart;
      break;
    case 'bar':
      ChartComponent = BarChart;
      break;
    default:
      ChartComponent = () => <div>Unhandled chart type</div>;
  }

  if (!isLoading && error) {
    return (
      <Flex justifyCenter paddingSize='medium'>
        <Text>{error.message}</Text>
      </Flex>
    );
  }

  if (!isLoading && !reportData?.dataSeries.length) {
    return (
      <Flex justifyCenter paddingSize='medium'>
        <Text>There is no data to display</Text>
      </Flex>
    );
  }
  return (
    <>
      {isLoading ? (
        <Flex justifyCenter paddingSize='medium'>
          <SpinnerLoader text='We are loading data...' />
        </Flex>
      ) : (
        <ChartComponent
          height={600}
          axis={reportData.axis}
          seriesData={reportData.dataSeries}
        />
      )}
    </>
  );
}
